/**
 * Funções puras para cálculo do Semáforo Climático de Pulverização.
 *
 * Regras de prioridade: Vermelho > Amarelo > Verde (prevalece sempre a mais crítica).
 *
 * Parâmetros esperados da API OpenWeatherMap (endpoint /forecast):
 *   - item.main.temp      → temperatura em °C (já em °C pois usamos units=metric)
 *   - item.rain["3h"]     → precipitação acumulada no intervalo de 3h, em mm (ausente = 0mm, sem chuva)
 *   - item.pop            → probabilidade de chuva (0 a 1)
 *   - item.wind.speed     → velocidade do vento em m/s
 *   - item.main.humidity  → umidade relativa em %
 */

const STATUS = {
  GREEN: "green",
  YELLOW: "yellow",
  RED: "red",
};

/** Retorna o status mais crítico dentre dois. */
const maisGrave = (a, b) => {
  const ordem = [STATUS.GREEN, STATUS.YELLOW, STATUS.RED];
  return ordem.indexOf(a) >= ordem.indexOf(b) ? a : b;
};

/**
 * Avalia a temperatura (em °C, já convertida via units=metric).
 * verde: acima de 18°C até 30°C
 * amarelo: 11 a 18°C, ou acima de 30°C até 35°C
 * vermelho: abaixo de 11°C, ou acima de 35°C
 */
const avaliarTemperatura = (tempC) => {
  if (tempC == null) return { status: STATUS.YELLOW, dataInsufficient: true };
  if (tempC < 11) return { status: STATUS.RED };
  if (tempC <= 18) return { status: STATUS.YELLOW };
  if (tempC <= 30) return { status: STATUS.GREEN };
  if (tempC <= 35) return { status: STATUS.YELLOW };
  return { status: STATUS.RED };
};

/**
 * Avalia a precipitação acumulada (em mm, item.rain["3h"]).
 * verde: 0mm | amarelo: acima de 0 até 4mm | vermelho: acima de 4mm
 * Ausência do campo `rain` na API significa "sem chuva nesse intervalo" (0mm),
 * não falta de dado - não marca dataInsufficient.
 */
const avaliarPrecipitacao = (mm) => {
  const valor = mm ?? 0;
  if (valor === 0) return { status: STATUS.GREEN };
  if (valor <= 4) return { status: STATUS.YELLOW };
  return { status: STATUS.RED };
};

/**
 * Avalia a probabilidade de chuva (pop de 0 a 1 → convertido para %).
 * verde: 0% | amarelo: 1 a 60% | vermelho: acima de 60%
 */
const avaliarChuva = (pop) => {
  if (pop == null) return { status: STATUS.YELLOW, dataInsufficient: true };
  const pct = pop * 100;
  if (pct === 0) return { status: STATUS.GREEN };
  if (pct <= 60) return { status: STATUS.YELLOW };
  return { status: STATUS.RED };
};

/**
 * Avalia a velocidade do vento (em m/s → converte para km/h multiplicando por 3.6).
 * verde: 3 a 10 km/h
 * amarelo: abaixo de 3 km/h, ou acima de 10 até 15 km/h
 * vermelho: acima de 15 km/h
 */
const avaliarVento = (speedMs) => {
  if (speedMs == null) return { status: STATUS.YELLOW, dataInsufficient: true };
  const kmh = speedMs * 3.6;
  if (kmh > 15) return { status: STATUS.RED };
  if (kmh >= 3 && kmh <= 10) return { status: STATUS.GREEN };
  return { status: STATUS.YELLOW };
};

/**
 * Avalia a umidade relativa (em %).
 * verde: acima de 60% | amarelo: 40 a 60% | vermelho: abaixo de 40%
 */
const avaliarUmidade = (humidity) => {
  if (humidity == null) return { status: STATUS.YELLOW, dataInsufficient: true };
  if (humidity < 40) return { status: STATUS.RED };
  if (humidity <= 60) return { status: STATUS.YELLOW };
  return { status: STATUS.GREEN };
};

/**
 * Calcula o semáforo para um único item de hora da previsão.
 *
 * @param {Object} horaData - Item da lista forecast (item de weatherData.list)
 * @returns {'green'|'yellow'|'red'}
 */
export const calcularSemaforoPorHora = (horaData) => {
  if (!horaData) return STATUS.YELLOW;

  const temp = horaData.main?.temp;
  const precipitacaoMm = horaData.rain?.["3h"];
  const pop = horaData.pop;
  const windSpeed = horaData.wind?.speed;
  const humidity = horaData.main?.humidity;

  const temp_r = avaliarTemperatura(temp);
  const precipitacao_r = avaliarPrecipitacao(precipitacaoMm);
  const chuva_r = avaliarChuva(pop);
  const vento_r = avaliarVento(windSpeed);
  const umidade_r = avaliarUmidade(humidity);

  let statusFinal = STATUS.GREEN;
  statusFinal = maisGrave(statusFinal, temp_r.status);
  statusFinal = maisGrave(statusFinal, precipitacao_r.status);
  statusFinal = maisGrave(statusFinal, chuva_r.status);
  statusFinal = maisGrave(statusFinal, vento_r.status);
  statusFinal = maisGrave(statusFinal, umidade_r.status);

  // precipitacao_r nunca entra aqui - ausência do campo é dado válido (0mm), não dado faltando.
  const dataInsufficient =
    temp_r.dataInsufficient ||
    chuva_r.dataInsufficient ||
    vento_r.dataInsufficient ||
    umidade_r.dataInsufficient ||
    false;

  return dataInsufficient ? STATUS.YELLOW : statusFinal;
};

/**
 * Calcula o semáforo geral a partir da lista completa de forecast.
 *
 * @param {Object} forecast - Objeto retornado pela OpenWeatherMap (/forecast), com forecast.list
 * @returns {{ status: 'green'|'yellow'|'red', detalhes: Object }}
 */
export const calcularSemaforoGeral = (forecast) => {
  if (!forecast?.list?.length) {
    return {
      status: STATUS.YELLOW,
      detalhes: { dataInsufficient: true },
    };
  }

  let statusGeral = STATUS.GREEN;
  let totalRed = 0;
  let totalYellow = 0;
  let totalGreen = 0;

  forecast.list.forEach((item) => {
    const s = calcularSemaforoPorHora(item);
    if (s === STATUS.RED) totalRed++;
    else if (s === STATUS.YELLOW) totalYellow++;
    else totalGreen++;
    statusGeral = maisGrave(statusGeral, s);
  });

  return {
    status: statusGeral,
    detalhes: {
      totalHoras: forecast.list.length,
      totalRed,
      totalYellow,
      totalGreen,
    },
  };
};

// Avaliações por fator isolado - usadas no tooltip de cada pastilha (ver
// weather-traffic-light/index.js) pra colorir cada métrica (Temperatura,
// Precipitação, Probabilidade de chuva, Vento, Umidade) com o status
// individual dela, não só o status geral já mais crítico (calcularSemaforoPorHora).
export {
  avaliarTemperatura,
  avaliarPrecipitacao,
  avaliarChuva,
  avaliarVento,
  avaliarUmidade,
};

export default {
  calcularSemaforoPorHora,
  calcularSemaforoGeral,
};
